import {
	publish,
	useCustomerId,
	useCustomerIdRef,
	useMqttTopic,
} from "@ptx-autonomous/libs/api";
import {
	ConnectorType,
	useFlightpathServiceHttp,
} from "@ptx-autonomous/libs/flightpath-service-api";
import {
	ImplementType,
	type Api,
	type ImplementActiveLookaheadMessage,
	type ImplementV2Model,
	type JobInfo,
	type RosSteeringCalibration,
} from "@ptx-autonomous/libs/flightpath-service-api-models";
import { Distance } from "@ptx-autonomous/libs/units";
import { convertMetersToInches } from "../utils/unit-conversion";
import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import type {
	ImplementAppParams,
	ImplementOptions,
	ImplementWithCalibration,
} from "../types";
import { Guid } from "typescript-guid";
import {
	computed,
	toRef,
	unref,
	type ComputedRef,
	type MaybeRef,
	type Ref,
} from "vue";
import { useImplementCalibrationsForMachine } from "./machine-model";

export function createImplementModel(
	options: ImplementOptions,
	id?: string,
): ImplementV2Model {
	const implementAxleToHitch = options.implementAxleToHitch;
	const toolEngagementPointToHitch = options.toolEngagementToHitch;
	const width = options.width;
	const overlap = new Distance(options.overlap, "in").m();

	const implementV2: ImplementV2Model = {
		CustomerId: useCustomerId(),
		NavigationReferencePoint: {
			ReferencePointX: 0,
			ReferencePointY: 0,
			ReferencePointZ: 0,
		},
		ConnectorReferencePointTractor: {
			ReferencePointX: 0,
			ReferencePointZ: 0,
		},
		ConnectorReferencePointImplement: {
			ReferencePointX: implementAxleToHitch,
			ReferencePointZ: 0,
		},
		ImplementId: id ?? Guid.create().toString(),
		ImplementName: options.name ?? "Unnamed Implement",
		Bins: [],
		Booms: [
			{
				ParentIndex: 0,
				ElementIndex: 6,
				SectionControlOverlapThreshold: 1,
				SectionControlTurnOffMilliseconds: secondsToMillisecondsAndTrim(
					options.lookAheadSecondsEntering ?? 0,
				),
				SectionControlTurnOnMilliseconds: secondsToMillisecondsAndTrim(
					options.lookAheadSecondsExiting ?? 0,
				),
				SetPointRate: 0,
				LeaveImplementDownOnTurns: options.leaveImplementDownOnTurns,
			},
		],
		Connectors:
			options.connectorType === "three-point"
				? [
						{
							ParentIndex: 0,
							ConnectorType: ConnectorType.ThreePointHitchMounted,
						},
					]
				: [
						{
							ParentIndex: 0,
							ConnectorType: ConnectorType.TractorDrawBar,
						},
					],
		Sections: [
			{
				ParentIndex: 6,
				DeviceElementReferencePoint: {
					ReferencePointX:
						implementAxleToHitch -
						toolEngagementPointToHitch -
						options.workLength / 2,
					ReferencePointY: 0,
					ReferencePointZ: 0,
				},
				ElementIndex: 7,
				ElementWorkingWidth: width,
				ActualWorkingLength: options.workLength,
				SectionControlTurnOffMilliseconds: 0,
				SectionControlTurnOnMilliseconds: 0,
			},
		],
		ApplicationParameters: JSON.stringify({
			overlap: overlap,
			workLength: options.workLength,
			totalLength: options.totalLength,
			totalWidth: options.totalWidth,
			raiseImplementWhenPaused: options.raiseImplementWhenPaused,
			swathExtensionPercentage: options.swathExtensionPercentage,
			implementKitInstalled: options.implementKitInstalled,
		}),
		ImplementType: options.implementType,
	};

	return implementV2;
}

export function optionsFromImplementModel(
	implement: ImplementV2Model | undefined,
): ImplementOptions | undefined {
	if (!implement) return undefined;
	const appParams = implement.ApplicationParameters
		? (JSON.parse(implement.ApplicationParameters) as ImplementAppParams)
		: {
				overlap: 0,
				workLength: 0,
				totalLength: 0,
			};

	const workingLength =
		implement.Sections?.[0].ActualWorkingLength ?? appParams.workLength;
	const implementAxleToHitch =
		implement.ConnectorReferencePointImplement!.ReferencePointX ?? 0;
	const sectionReferencePointX =
		implement.Sections?.[0].DeviceElementReferencePoint?.ReferencePointX ??
		0;
	return {
		name: implement.ImplementName ?? "Unnamed Implement",
		width: implement.Sections?.[0].ElementWorkingWidth ?? 0,
		implementAxleToHitch,
		toolEngagementToHitch:
			implementAxleToHitch - sectionReferencePointX - workingLength / 2,
		overlap: convertMetersToInches(appParams.overlap),
		lookAheadSecondsEntering:
			(implement.Booms?.[0].SectionControlTurnOffMilliseconds ?? 0) /
			1000,
		lookAheadSecondsExiting:
			(implement.Booms?.[0].SectionControlTurnOnMilliseconds ?? 0) / 1000,
		connectorType:
			implement.Connectors?.[0].ConnectorType ===
			ConnectorType.ThreePointHitchMounted
				? "three-point"
				: "pull",
		implementType: implement.ImplementType,
		workLength: appParams.workLength,
		totalLength: appParams.totalLength ?? 0,
		totalWidth: appParams.totalWidth ?? 0,
		leaveImplementDownOnTurns:
			implement.Booms?.[0].LeaveImplementDownOnTurns ?? false,
		raiseImplementWhenPaused: appParams.raiseImplementWhenPaused ?? true,
		swathExtensionPercentage: appParams.swathExtensionPercentage ?? 0,
		implementKitInstalled: appParams.implementKitInstalled ?? false,
	};
}

export function getImplementSwathExtension(
	implement: ImplementV2Model,
	rearAxleToHitch?: number,
) {
	// This is only really used by the web-planner because the user doesn't have a machine to select so it provides a value from the settings
	if (rearAxleToHitch !== undefined) {
		return (
			(implement.ConnectorReferencePointImplement?.ReferencePointX ?? 0) +
			rearAxleToHitch +
			(implement.NavigationReferencePoint?.ReferencePointX ?? 0)
		);
	}
	return (
		(implement.ConnectorReferencePointImplement?.ReferencePointX ?? 0) -
		(implement.ConnectorReferencePointTractor?.ReferencePointX ?? 0) +
		(implement.NavigationReferencePoint?.ReferencePointX ?? 0)
	);
}

export function useImplementsWithSelection(
	activeJob:
		| Ref<JobInfo | undefined | null>
		| ComputedRef<JobInfo | undefined | null>,
	machineId: MaybeRef<string | undefined>,
) {
	const http = useFlightpathServiceHttp();
	const selectedImplementId = computed(() => activeJob.value?.ImplementId);
	const implementListQuery = useMachineImplementsWithCalibrations(
		http,
		machineId,
	);
	const implementList = computed(() => implementListQuery.data.value);
	const selectedImplement = computed(() => {
		return implementList.value?.find(
			implement => implement.ImplementId === selectedImplementId.value,
		);
	});

	return {
		selectedImplement,
		selectedImplementId,
		implementList,
	};
}

export function useMachineImplements(
	api: Api<unknown>,
	machineId: MaybeRef<string | undefined>,
) {
	async function machineImplementsQuery(): Promise<ImplementV2Model[]> {
		const machineIdVal = unref(machineId);
		if (!machineIdVal) {
			return [];
		}
		const res = await api.implementV2.machineDetail(machineIdVal);
		const implementsList = res.data as ImplementV2Model[];
		return implementsList.filter(
			implement =>
				implement.ImplementType !== ImplementType.CombineHeader,
		);
	}

	return useQuery({
		queryKey: ["machineImplementList", machineId],
		queryFn: () => machineImplementsQuery(),
		enabled: computed(() => !!unref(machineId)),
	});
}

export function useMachineImplementsWithCalibrations(
	api: Api<unknown>,
	machineId: MaybeRef<string | undefined>,
) {
	const {
		data: implementList,
		refetch: refetchImplements,
		isFetched: isImplementListFetched,
		isFetching: isImplementListFetching,
	} = useMachineImplements(api, machineId);
	const {
		data: implementCalibrations,
		refetch: refetchCalibrations,
		isFetched: isImplementCalibrationsFetched,
		isFetching: isImplementCalibrationsFetching,
	} = useImplementCalibrationsForMachine(api, toRef(machineId));

	const data = computed<ImplementWithCalibration[] | undefined>(() => {
		const calibrations = implementCalibrations.value ?? [];
		if (!implementList.value) {
			return undefined;
		}
		return implementList.value.map(implement => {
			const calibration =
				calibrations.find(
					cal => cal.ImplementId === implement.ImplementId,
				) ?? null;
			return {
				...implement,
				calibration,
			};
		});
	});

	function refetch() {
		refetchImplements();
		refetchCalibrations();
	}

	const isFetched = computed(() => {
		return (
			isImplementListFetched.value && isImplementCalibrationsFetched.value
		);
	});

	const isFetching = computed(() => {
		return (
			isImplementListFetching.value ||
			isImplementCalibrationsFetching.value
		);
	});

	return { data, refetch, isFetched, isFetching };
}

export function useImplementsV2(api: Api<unknown>) {
	async function implementsQuery(): Promise<ImplementV2Model[]> {
		const res = await api.implementV2.implementV2List();
		const implementsList = res.data as ImplementV2Model[];
		return implementsList.filter(
			implement =>
				implement.ImplementType !== ImplementType.CombineHeader,
		);
	}

	return useQuery({
		queryKey: ["implementList"],
		queryFn: () => implementsQuery(),
	});
}

export function useMissionImplementQuery(
	api: Api<unknown>,
	missionId: MaybeRef<string | undefined>,
) {
	return useQuery({
		queryKey: ["implementList", missionId],
		queryFn: async () => {
			const res = await api.missions.implementList(unref(missionId)!);
			return res.data;
		},
		enabled: computed(() => !!unref(missionId)),
	});
}

export function useCreateImplementV2Mutation(api: Api<unknown>) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (implement: ImplementV2Model) => {
			const res = await api.implementV2.implementV2Create(implement);
			return res.data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["implementList"] });
			queryClient.invalidateQueries({
				queryKey: ["machineImplementList"],
			});
		},
	});
}

export function useResetLookaheads(api: Api<unknown>) {
	return useMutation({
		mutationFn: async (options: {
			implementId: string;
			machineId: string;
		}) => {
			const res = await api.implements.refreshCalibrationsCreate(
				options.implementId,
				options.machineId,
			);
			return res.data;
		},
	});
}

export function useUpdateImplementV2Mutation(api: Api<unknown>) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (implement: ImplementV2Model) => {
			const res = await api.implementV2.implementV2Update(implement);
			return res.data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["implementList"] });
			queryClient.invalidateQueries({
				queryKey: ["machineImplementList"],
			});
		},
	});
}

function secondsToMillisecondsAndTrim(value: number) {
	return Math.max(0, Math.round(value * 1000));
}

export function useDeleteImplementV2Mutation(api: Api<unknown>) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (implementId: string) => {
			const res = await api.implementV2.implementV2Delete(implementId);
			return res.data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["implementList"] });
			queryClient.invalidateQueries({
				queryKey: ["machineImplementList"],
			});
		},
	});
}

export async function sendTurningRadiusCalibrationMessage(
	requestGuid: string,
	machineId: string,
	apiVersion: string,
	calibrateImplementTurningRadius: boolean,
) {
	await publish(
		`${apiVersion}/${useCustomerId()}/${machineId}/flightpath_turning_radius_calibration`,
		{
			header: { stamp: { sec: 0, nanosec: 0 }, frame_id: "" },
			request_uuid: requestGuid,
			calibrate_min_turning_radius: calibrateImplementTurningRadius,
		},
	);
}

export type TurningRadiusCalibrationMessage = {
	calibrated_min_turning_radius: number;
	calibration_state: number;
	percent_completed: number;
	request_uuid: string;
};

export function useTurningRadiusCalibration(
	apiVersion: Ref<string>,
	machineId: Ref<string | undefined> | ComputedRef<string | undefined>,
) {
	const topic = computed(() =>
		machineId.value
			? `${apiVersion.value}/${useCustomerIdRef().value}/${machineId.value}/ros_turning_radius_calibration`
			: undefined,
	);
	const message = useMqttTopic<TurningRadiusCalibrationMessage>(topic);
	return message;
}

export async function sendImplementTimeCalibrationMessage(
	requestGuid: string,
	machineId: string,
	apiVersion: string,
	calibrateImplementDownUpTime: boolean,
) {
	await publish(
		`${apiVersion}/${useCustomerId()}/${machineId}/flightpath_implement_time_calibration`,
		{
			header: { stamp: { sec: 0, nanosec: 0 }, frame_id: "" },
			request_uuid: requestGuid,
			calibrate_implement_down_up_time: calibrateImplementDownUpTime,
		},
	);
}

export type ImplementTimeCalibrationMessage = {
	request_uuid: string;
	implement_down_time: number;
	implement_raised_time: number;
};

export function useImplementTimeCalibration(
	apiVersion: Ref<string>,
	machineId: Ref<string | undefined> | ComputedRef<string | undefined>,
) {
	const topic = computed(() =>
		machineId.value
			? `${apiVersion.value}/${useCustomerIdRef().value}/${machineId.value}/ros_implement_time_calibration`
			: undefined,
	);
	const message = useMqttTopic<ImplementTimeCalibrationMessage>(topic);
	return message;
}

export type ImplementStatusMessage = {
	status: number;
	scale_weight: number;
};

export function useImplementStatus(
	apiVersion: Ref<string>,
	machineId: Ref<string | undefined> | ComputedRef<string | undefined>,
) {
	const topic = computed(() =>
		machineId.value
			? `${apiVersion.value}/${useCustomerIdRef().value}/${machineId.value}/implement_status`
			: undefined,
	);
	const message = useMqttTopic<ImplementStatusMessage>(topic);
	return message;
}

// Implement Height Sensor Calibration

export const ImplementSensorCalibrationStep = {
	START_SENSOR_CALIBRATION: 1,
	SET_GROUND_POSITION: 2,
	SET_ZERO_POSITION: 3,
	START_DYNAMIC_CALIBRATION_SEQUENCE: 4,
	STOP_CALIBRATION_ROUTINE: 5,
} as const;

export const ImplementSensorCalibrationResult = {
	INVALID_REQUEST: 0,
	ROUTINE_NOT_STARTED: 1,
	OUT_OF_ORDER: 2,
	NOT_IN_MACHINE_CALIBRATION_STATE: 3,
	UNKNOWN_FAILURE: 4,
	SUCCESSFUL: 5,
	INITIAL_CONDITIONS_NOT_MET: 6,
	CALIBRATION_HAS_FAILED: 7,
} as const;

export const ImplementSensorCalibrationCurrentStep = {
	STARTING_SENSOR_CALIBRATION: 0,
	WAITING_TO_SET_GROUND_POSITION: 1,
	SETTING_GROUND_POSITION: 2,
	WAITING_TO_SET_ZERO_POSITION: 3,
	SETTING_ZERO_POSITION: 4,
	WAITING_TO_START_DYNAMIC_CALIBRATION: 5,
	WAITING_FOR_MOTION: 6,
	RAISING_IMPLEMENT: 7,
	SETTING_RAISED_POSITION: 8,
	LOWERING_IMPLEMENT: 9,
	SETTING_LOWERED_POSITION: 10,
	SENSOR_CALIBRATION_COMPLETE: 11,
} as const;

export const ImplementSensorCalibrationFailureType = {
	EXECUTING: 0,
	INITIAL_CONDITIONS_NOT_MET: 1,
	MACHINE_NOT_IN_CALIBRATION_STATE: 2,
	CALIBRATE_GROUND_THRESHOLD_ERROR: 3,
	CALIBRATE_LOWER_THRESHOLD_ERROR: 4,
	CALIBRATE_RAISED_THRESHOLD_ERROR: 5,
	IMPLEMENT_CONTROL_TIMED_OUT: 6,
	UNKNOWN_FAILURE: 7,
	MACHINE_IN_MOTION: 8,
	MACHINE_UNDER_SPEED_THRESHOLD: 9,
} as const;

export type ImplementSensorCalibrationRequestAckMessage = {
	calibration_uuid: string;
	requested_step: number;
	result: number;
};

export type ImplementSensorCalibrationFeedbackMessage = {
	calibration_uuid: string;
	current_step: number;
	failure_type: number;
	measured_lowered_height_threshold_percent: number;
	measured_ground_height_threshold_percent: number;
	measured_raised_height_threshold_percent: number;
};

export async function sendImplementSensorCalibrationRequest(
	calibrationUuid: string,
	requestedStep: number,
	machineId: string,
	apiVersion: string,
) {
	await publish(
		`${apiVersion}/${useCustomerId()}/${machineId}/implement_sensor_calibration_routine_request`,
		{
			calibration_uuid: calibrationUuid,
			requested_step: requestedStep,
		},
	);
}

export function useImplementSensorCalibrationRequestAck(
	apiVersion: Ref<string>,
	machineId: Ref<string | undefined> | ComputedRef<string | undefined>,
) {
	const topic = computed(() =>
		machineId.value
			? `${apiVersion.value}/${useCustomerIdRef().value}/${machineId.value}/implement_sensor_calibration_routine_request_ack`
			: undefined,
	);
	return useMqttTopic<ImplementSensorCalibrationRequestAckMessage>(topic);
}

export function useImplementSensorCalibrationFeedback(
	apiVersion: Ref<string>,
	machineId: Ref<string | undefined> | ComputedRef<string | undefined>,
) {
	const topic = computed(() =>
		machineId.value
			? `${apiVersion.value}/${useCustomerIdRef().value}/${machineId.value}/implement_sensor_calibration_routine_feedback`
			: undefined,
	);
	return useMqttTopic<ImplementSensorCalibrationFeedbackMessage>(topic);
}

// Implement Timing Calibration

export const ImplementTimingCalibrationStep = {
	START_CALIBRATION_ROUTINE: 1,
	STOP_CALIBRATION_ROUTINE: 2,
} as const;

export const ImplementTimingCalibrationPhase = {
	STARTING: 1,
	WAITING_FOR_MOTION: 2,
	RAISING: 3,
	LOWERING: 4,
	CALIBRATION_COMPLETE: 5,
} as const;

export const ImplementTimingCalibrationFailureType = {
	EXECUTING: 0,
	INITIAL_CONDITIONS_NOT_MET: 1,
	MACHINE_STATE_INVALID: 2,
	IMPLEMENT_CONTROL_TIMED_OUT: 3,
	MACHINE_FELL_UNDER_SPEED_THRESHOLD: 4,
	IMPLEMENT_HEIGHT_SENSOR_UNCALIBRATED: 5,
	UNKNOWN_FAILURE: 6,
} as const;

export type ImplementTimingCalibrationFeedbackMessage = {
	calibration_uuid: string;
	current_phase: number;
	current_cycle_index: number;
	total_cycles: number;
	failure_type: number;
	current_measured_raise_time_s: number;
	current_measured_lower_time_s: number;
};

export async function sendImplementTimingCalibrationRequest(
	calibrationUuid: string,
	requestedStep: number,
	numberOfRaiseLowerCycles: number,
	machineId: string,
	apiVersion: string,
) {
	await publish(
		`${apiVersion}/${useCustomerId()}/${machineId}/implement_timing_calibration_routine_request`,
		{
			calibration_uuid: calibrationUuid,
			number_of_raise_lower_cycles: numberOfRaiseLowerCycles,
			requested_step: requestedStep,
		},
	);
}

// Machine Calibration State Transition

export const MachineCalibrationStateTransitionResult = {
	ERROR: 0,
	SUCCESSFUL: 1,
	NOT_IN_CORRECT_STATE: 2,
	REQUIREMENTS_NOT_MET_FOR_TRANSITION: 3,
} as const;

export type MachineCalibrationStateRequestAckMessage = {
	request_uuid: string;
	result: number;
};

export async function sendMachineCalibrationStateRequest(
	requestUuid: string,
	machineId: string,
	apiVersion: string,
) {
	await publish(
		`${apiVersion}/${useCustomerId()}/${machineId}/request_machine_calibration_state`,
		{ request_uuid: requestUuid },
	);
}

export function useMachineCalibrationStateRequestAck(
	apiVersion: Ref<string>,
	machineId: Ref<string | undefined> | ComputedRef<string | undefined>,
) {
	const topic = computed(() =>
		machineId.value
			? `${apiVersion.value}/${useCustomerIdRef().value}/${machineId.value}/request_machine_calibration_state_ack`
			: undefined,
	);
	return useMqttTopic<MachineCalibrationStateRequestAckMessage>(topic);
}

export function useImplementTimingCalibrationFeedback(
	apiVersion: Ref<string>,
	machineId: Ref<string | undefined> | ComputedRef<string | undefined>,
) {
	const topic = computed(() =>
		machineId.value
			? `${apiVersion.value}/${useCustomerIdRef().value}/${machineId.value}/implement_timing_calibration_routine_feedback`
			: undefined,
	);
	return useMqttTopic<ImplementTimingCalibrationFeedbackMessage>(topic);
}

export function useImplementActiveLookahead(
	api: Api<unknown>,
	implementId: MaybeRef<string | undefined>,
	machineId: MaybeRef<string | undefined>,
) {
	async function getLatestActiveLookahead(): Promise<ImplementActiveLookaheadMessage | null> {
		const implementIdValue = unref(implementId);
		const machineIdValue = unref(machineId);
		if (!implementIdValue || !machineIdValue) return null;
		try {
			const { data } = await api.implementv2.activeLookaheadsDetail(
				implementIdValue,
				machineIdValue,
			);
			return data;
		} catch (error) {
			if ((error as { status?: number }).status === 404) {
				return null;
			}
			throw error;
		}
	}

	return useQuery({
		queryKey: [`implement-active-lookahead`, implementId, machineId],
		queryFn: getLatestActiveLookahead,
		enabled: computed(() => !!unref(implementId) && !!unref(machineId)),
		refetchInterval: 30 * 1000,
	});
}

export async function fetchRosSteeringCalibration(
	api: Api<unknown>,
	machineId: string | undefined,
): Promise<RosSteeringCalibration | undefined> {
	if (!machineId) return undefined;

	try {
		const res = await api.ros.calibrationSteeringMachineDetail(machineId);
		return res.data;
	} catch (error) {
		if ((error as { status?: number }).status === 404) {
			return undefined;
		}
		throw error;
	}
}
